/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-unused-expressions */
import React, {
  useState,
  useCallback,
  useEffect,
  ReactElement,
  JSXElementConstructor,
  useContext,
} from 'react';
import { Text, Icon } from '@oneaudi/unified-web-components';
import { UeContainer } from '@oneaudi/falcon-tools';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import NavItems from './NavItems';
import NavBarWrapper from './NavBarWrapper';
import {
  StyledArrowIcon,
  StyledInput,
  StyledOverarchingContent,
  StyledOverarchingName,
  StyledSubMenu,
  StyledText,
} from './NavigationStyles';
import { TrackingServiceContext, useInPageDispatch, useInPageNavContext } from '../../context';
import { INPAGENAV_ACTIONS } from '../../reducers/inPageNavReducer';
import usePriorityNav from '../../hooks/use-priority-nav';
import { getTopLevelActiveItemHref, hasSubLinks, isParentElement } from '../../utils/utils';
import OverViewLabel from './OverViewLabel';
import { ActiveLinks } from '../../../types';
import PriorityDropDownItem from './PriorityDropDownItem';
import { useTracking } from '../../hooks/use-tracking';
import {
  subLinksRelatedElements,
  topLevelRelatedElements,
  trackToggleEvent,
} from '../../utils/tracking-utils';
import { handleKeyDown } from '../../utils/a11y';
import useEventListener from '../../hooks/use-event-listener';

interface IProps {
  visible: boolean;
}

type OverarchingContentProps = {
  overarchingName?: string;
};

const OverarchingContent = (props: OverarchingContentProps) => {
  const { overarchingName } = props;
  const {
    parentPageInpageNavigation,
    sameLevelInpageNavigation,
    sameLevelActiveLink,
    topLevelActiveLink,
    secondLevelActiveLink,
    isLoadedFromUrl,
    impressionTracking,
  } = useInPageNavContext();
  const [pageLabel, setPageLabel] = useState<string>('');
  const { trackingService } = useContext(TrackingServiceContext);
  const tracking = useTracking(trackingService);
  const trackingRelatedElements = topLevelRelatedElements(sameLevelInpageNavigation);

  useEffect(() => {
    if (isLoadedFromUrl) {
      if (sameLevelActiveLink) {
        setPageLabel(sameLevelActiveLink?.text || '');
      }
      if (topLevelActiveLink && secondLevelActiveLink) {
        setPageLabel(`${topLevelActiveLink?.text || ''} > ${secondLevelActiveLink?.text || ''}`);
      }
    } else {
      setPageLabel(parentPageInpageNavigation?.fields?.subLevelParentLabel || '');
    }
  }, [isLoadedFromUrl, topLevelActiveLink]);

  return (
    <>
      <StyledInput
        type="checkbox"
        name="chckOverview"
        id="chckOverview"
        onClick={(e: React.MouseEvent<HTMLInputElement>) => {
          const inputCheckbox = e.target as HTMLInputElement;
          // Tracking for Mobile Parent Accordion menu
          const isChecked: boolean = inputCheckbox.checked;
          trackToggleEvent(
            tracking,
            `inpage navigation - ${isChecked ? 'open' : 'close'} mobile sub navigation`,
            [overarchingName || '', pageLabel],
            impressionTracking?.value,
            '',
            '',
            isChecked ? trackingRelatedElements : [],
          );
        }}
      />
      <StyledOverarchingName htmlFor="chckOverview">
        <StyledOverarchingContent>
          <StyledText tag="p" className="title">
            {overarchingName}
          </StyledText>
          {pageLabel !== '' ? <Text tag="span">{pageLabel}</Text> : <span>&nbsp;</span>}
        </StyledOverarchingContent>
        <StyledArrowIcon>
          <Icon name="arrow-up" size="s" />
        </StyledArrowIcon>
      </StyledOverarchingName>
    </>
  );
};

const TopLevelNav = (props: IProps) => {
  const { visible } = props;
  const dispatch = useInPageDispatch();
  const [activePriortyDropDown, setActivePriortyDropDown] = useState(false);
  const [refNavUpdated, setRefNavUpdated] = useState<React.RefObject<HTMLElement>>();
  const [TopLevelNavRef, setTopLevelNavRef] = useState<React.RefObject<HTMLElement>>();

  const handlePriorityDropDownClick = useCallback(() => {
    setActivePriortyDropDown((prev) => !prev);
  }, []);

  const {
    topLevelActiveLink,
    sameLevelInpageNavigation,
    accordionLabel,
    parentPageInpageNavigation,
    priorityNavActive,
    priorityNavLoaded,
    subNavActive,
    impressionTracking,
  } = useInPageNavContext();
  const { trackingService } = useContext(TrackingServiceContext);
  const tracking = useTracking(trackingService);
  const isMobile = useMediaQuery('(max-width: 1024px)');

  useEffect(() => {
    if (dispatch) {
      const interaction = Number(priorityNavActive && priorityNavActive?.interaction + 1);
      dispatch({
        type: INPAGENAV_ACTIONS.PRIORITY_NAV_ACTIVE,
        payload: {
          priorityNavActive: {
            ...priorityNavActive,
            interaction,
            isDesktop: window?.innerWidth >= 1024,
            active: activePriortyDropDown,
          },
        },
      });
    }
  }, [activePriortyDropDown]);

  const { popOverNav, activeLink, priorityItemslength } = usePriorityNav({
    refNavUpdated,
    TopLevelNavRef,
    activePriortyDropDown,
  }) as {
    popOverNav: ReactElement<unknown, string | JSXElementConstructor<unknown>> | null;
    activeLink: number;
    priorityItemslength: number;
  };
  const handleSelectedItem = useCallback(
    ({ id, index, text, subLevelChildLabel, target }: ActiveLinks) => {
      const href = getTopLevelActiveItemHref(sameLevelInpageNavigation, index);
      const subLinks = hasSubLinks(index, sameLevelInpageNavigation);
      const hasItemSubLinks = subLinks.length > 0;
      const active = index !== topLevelActiveLink?.index || Boolean(visible && hasItemSubLinks);

      if (dispatch) {
        dispatch({
          type: INPAGENAV_ACTIONS.TOP_LEVEL_ACTIVE_LINK,
          payload: {
            topLevelActiveLink: { id, index, text, subLevelChildLabel, href, target },
            subNavActive: {
              index,
              subLinks: {
                hasLinks: hasItemSubLinks,
                active,
                text: subLevelChildLabel || text,
              },
            },
          },
        });
      }

      // tracking for toggle subnav menu
      if (hasItemSubLinks) {
        const relatedElements = active ? subLinksRelatedElements(subLinks) : [];
        trackToggleEvent(
          tracking,
          `inpage navigation - ${active ? 'open' : 'close'} sub navigation`,
          [text, subLevelChildLabel || ''],
          impressionTracking?.value || '',
          href,
          index,
          relatedElements,
        );
      }
    },
    [dispatch, sameLevelInpageNavigation, subNavActive],
  );

  const handleListRef = useCallback(
    (ref?: React.RefObject<HTMLElement>) => {
      if (ref?.current?.classList.contains('inpage-nav-menu')) {
        setRefNavUpdated(ref);
      }
      if (ref?.current?.classList.contains('top-ul-list')) {
        setTopLevelNavRef(ref);
      }
    },
    [setTopLevelNavRef, setRefNavUpdated],
  );

  // a11y keyboard navigation
  useEventListener('keydown', (e: KeyboardEvent | Event) => {
    if (e instanceof KeyboardEvent) {
      if (TopLevelNavRef) handleKeyDown(e, TopLevelNavRef.current);
      // a11y mobile handle opening menus
      document.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        const inputCheckbox = checkbox as HTMLInputElement;
        if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'Space') {
          // @ts-expect-error
          if (e.target?.id === inputCheckbox.id) {
            e.preventDefault();
            inputCheckbox.checked = !inputCheckbox.checked;
            inputCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }
  });

  const priorityNavItemsLength =
    priorityItemslength !== sameLevelInpageNavigation?.length ? priorityItemslength : 0;

  return (
    <UeContainer propertyPath="sameLevelInpageNavigation" label="Navigation bar">
      {/* For mobile heading view */}
      {isMobile && <OverarchingContent overarchingName={accordionLabel} />}

      <StyledSubMenu id="menu">
        <NavBarWrapper visible handleListRef={handleListRef}>
          {isParentElement(parentPageInpageNavigation) && <OverViewLabel />}
          {sameLevelInpageNavigation?.map((item, i) => {
            const { fields } = item;
            return (
              <NavItems
                // eslint-disable-next-line react/no-array-index-key
                key={`nav-item-${i}`}
                id={item.id}
                fields={fields}
                index={i}
                hasParentElement={!!parentPageInpageNavigation}
                handleSelectedItem={handleSelectedItem}
                fieldsLength={sameLevelInpageNavigation.length}
                subNavigation={fields?.subLinks}
              />
            );
          })}
          <PriorityDropDownItem
            handlePriorityDropDownClick={handlePriorityDropDownClick}
            activePriortyDropDown={activePriortyDropDown}
            priorityNavLoaded={activeLink >= 0 && priorityNavLoaded}
            isMobile={!priorityNavActive?.isDesktop}
            priorityItemslength={priorityNavActive?.isDesktop ? priorityNavItemsLength : 0}
          />
        </NavBarWrapper>

        {popOverNav}
      </StyledSubMenu>
    </UeContainer>
  );
};

export default TopLevelNav;
